<template>
  <sme-card large title="Employee Impact">
    <app-loading v-if="loading" />

    <template v-else>
      <!-- Info Box -->
      <p v-if="hasSentimentData" class="mb-4">
        These insights reflect the voices of Stream members, gathered through our ongoing surveys.
      </p>

      <!-- Sentiment Stats Row -->
      <b-row v-if="hasSentimentData" class="mb-4">
        <b-col md="4" class="mb-3 mb-md-0">
          <sme-card class="h-100">
            <app-stat
              :stat="avgStressedPercent"
              :previous-stat="prevAvgStressedPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel less stressed (3m avg)"
              background-color="core-1-lighten-70"
              color="default"
              small
            />
            <p v-if="stressedRespondentCount" class="dashboard-impact__respondents">
              Based on {{ stressedRespondentCount }} responses
            </p>
          </sme-card>
        </b-col>
        <b-col md="4" class="mb-3 mb-md-0">
          <sme-card class="h-100">
            <app-stat
              :stat="avgControlPercent"
              :previous-stat="prevAvgControlPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel more in control (3m avg)"
              background-color="core-4-lighten-80"
              color="default"
              small
            />
            <p v-if="controlRespondentCount" class="dashboard-impact__respondents">
              Based on {{ controlRespondentCount }} responses
            </p>
          </sme-card>
        </b-col>
        <b-col md="4">
          <sme-card class="h-100">
            <app-stat
              :stat="avgEmployerPercent"
              :previous-stat="prevAvgEmployerPercent"
              :formatter="toNumber"
              suffix="%"
              description="feel positive about employer (3m avg)"
              background-color="success-lighten-80"
              color="default"
              small
            />
            <p v-if="employerRespondentCount" class="dashboard-impact__respondents">
              Based on {{ employerRespondentCount }} responses
            </p>
          </sme-card>
        </b-col>
      </b-row>

      <!-- Quotes Section -->
      <div v-if="quotes.length" class="quotes-section">
        <h6 class="mb-3">What your employees are saying</h6>
        <b-row>
          <b-col v-for="(quote, index) in displayedQuotes" :key="index" md="4" class="mb-3">
            <div class="quote-card p-3 h-100">
              <span class="quote-mark">&ldquo;</span>
              <p class="mb-0 fst-italic">{{ quote }}</p>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Empty State -->
      <p v-if="!hasSentimentData && !quotes.length" class="my-2 text-center text-muted">
        No impact data available yet.
      </p>
    </template>
  </sme-card>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import { toNumber } from '@/utils/common';

const loading = ref(true);
const stressedPercentages = ref([]);
const controlPercentages = ref([]);
const employerPercentages = ref([]);
const stressedTotalResponses = ref([]);
const controlTotalResponses = ref([]);
const employerTotalResponses = ref([]);
const quotes = ref([]);

const calculatePercentages = (positiveValues, totalValues) => {
  if (!positiveValues || !totalValues || positiveValues.length !== totalValues.length) {
    return [];
  }
  return positiveValues.map((positive, i) => {
    const total = totalValues[i] || 0;
    return total > 0 ? Math.round((positive / total) * 1000) / 10 : 0;
  });
};

const calculateRollingAverage = (values, months = 3, offset = 0) => {
  if (!values?.length) {
    return 0;
  }
  const recent = offset ? values.slice(-offset - months, -offset) : values.slice(-months);
  if (!recent.length) {
    return 0;
  }
  const sum = recent.reduce((acc, val) => acc + (val || 0), 0);
  return Math.round((sum / recent.length) * 10) / 10;
};

const avgStressedPercent = computed(() => calculateRollingAverage(stressedPercentages.value));
const avgControlPercent = computed(() => calculateRollingAverage(controlPercentages.value));
const avgEmployerPercent = computed(() => calculateRollingAverage(employerPercentages.value));

const prevAvgStressedPercent = computed(() => calculateRollingAverage(stressedPercentages.value, 3, 3));
const prevAvgControlPercent = computed(() => calculateRollingAverage(controlPercentages.value, 3, 3));
const prevAvgEmployerPercent = computed(() => calculateRollingAverage(employerPercentages.value, 3, 3));

const sumRecentValues = (values, months = 3) => {
  if (!values?.length) {
    return 0;
  }
  return values.slice(-months).reduce((acc, val) => acc + (val || 0), 0);
};

const stressedRespondentCount = computed(() => sumRecentValues(stressedTotalResponses.value));
const controlRespondentCount = computed(() => sumRecentValues(controlTotalResponses.value));
const employerRespondentCount = computed(() => sumRecentValues(employerTotalResponses.value));

const hasSentimentData = computed(() => {
  return (
    stressedPercentages.value.length > 0 || controlPercentages.value.length > 0 || employerPercentages.value.length > 0
  );
});

const displayedQuotes = computed(() => quotes.value.slice(0, 6));

const getImpactData = async () => {
  loading.value = true;

  try {
    const timeSeries = 'm2m';

    const [stressResponse, controlResponse, employerResponse, quotesResponse] = await Promise.all([
      ApiClient.getPortalSentimentStress(timeSeries),
      ApiClient.getPortalSentimentControl(timeSeries),
      ApiClient.getPortalSentimentEmployer(timeSeries),
      ApiClient.getPortalQuotes(),
    ]);

    if (stressResponse?.results?.length) {
      const lessStress = stressResponse.results.find(result => result.id === 'less_stress');
      const totalResponses = stressResponse.results.find(result => result.id === 'total_responses');

      if (lessStress && totalResponses) {
        stressedPercentages.value = calculatePercentages(lessStress.values, totalResponses.values);
        stressedTotalResponses.value = totalResponses.values;
      }
    }

    if (controlResponse?.results?.length) {
      const moreControl = controlResponse.results.find(result => result.id === 'more_control');
      const totalResponses = controlResponse.results.find(result => result.id === 'total_responses');

      if (moreControl && totalResponses) {
        controlPercentages.value = calculatePercentages(moreControl.values, totalResponses.values);
        controlTotalResponses.value = totalResponses.values;
      }
    }

    if (employerResponse?.results?.length) {
      const positiveSentiment = employerResponse.results.find(result => result.id === 'positive_sentiment');
      const totalResponses = employerResponse.results.find(result => result.id === 'total_responses');

      if (positiveSentiment && totalResponses) {
        employerPercentages.value = calculatePercentages(positiveSentiment.values, totalResponses.values);
        employerTotalResponses.value = totalResponses.values;
      }
    }

    if (quotesResponse?.data?.length) {
      quotes.value = quotesResponse.data;
    }
  } catch (error) {
    console.error('Failed to fetch impact data:', error);
  }

  loading.value = false;
};

onBeforeMount(() => getImpactData());
</script>

<style scoped>
.dashboard-impact__respondents {
  font-size: 11px;
  color: var(--gray-500);
  margin: 0.5rem 0 0;
}

.quote-card {
  background: var(--palette-color-base-white);
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  position: relative;
  padding-top: 2rem !important;
}

.quote-mark {
  position: absolute;
  top: 0.5rem;
  left: 0.75rem;
  font-size: 2rem;
  line-height: 1;
  color: var(--palette-color-brand-primary);
  font-family: Georgia, serif;
}

.quotes-section h6 {
  color: var(--gray-700);
  font-weight: 600;
}
</style>
